import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getVideoPlaybackCandidates } from "../../../utils/videoPlaybackUrls";

/**
 * Paused-frame thumbnail filling the card — click opens fullscreen preview (no play icon overlay).
 */
export default function VideoThumbnailButton({
  rawUrl,
  onOpen,
  className,
  ariaLabel,
  fallbackClassName,
}) {
  const candidates = useMemo(
    () => getVideoPlaybackCandidates(rawUrl ?? ""),
    [rawUrl]
  );
  const [index, setIndex] = useState(0);
  const [exhausted, setExhausted] = useState(false);
  const videoRef = useRef(null);
  const n = candidates.length;
  const src = n > 0 && index < n ? candidates[index] : null;

  useEffect(() => {
    setIndex(0);
    setExhausted(false);
  }, [rawUrl, n]);

  const seekFrame = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    try {
      const d = Number(v.duration);
      if (Number.isFinite(d) && d > 0) {
        v.currentTime = Math.min(Math.max(d * 0.02, 0.05), d * 0.25);
      } else {
        v.currentTime = 0.1;
      }
    } catch {
      /* ignore seek errors */
    }
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !src || exhausted) return;
    const onLoaded = () => {
      seekFrame();
      v.pause();
    };
    v.addEventListener("loadeddata", onLoaded);
    v.addEventListener("loadedmetadata", seekFrame);
    return () => {
      v.removeEventListener("loadeddata", onLoaded);
      v.removeEventListener("loadedmetadata", seekFrame);
    };
  }, [src, exhausted, seekFrame]);

  const onVideoError = () => {
    setIndex((prev) => {
      const next = prev + 1;
      if (next < n) return next;
      setExhausted(true);
      return prev;
    });
  };

  const open = () => {
    videoRef.current?.pause();
    onOpen?.();
  };

  const onKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      open();
    }
  };

  if (!String(rawUrl ?? "").trim()) {
    return null;
  }

  if (!n || exhausted || !src) {
    return (
      <button
        type="button"
        className={`video-card-thumb-hit ${className ?? ""}`}
        onClick={open}
        onKeyDown={onKey}
        aria-label={ariaLabel || "Open video"}
      >
        <div
          className={fallbackClassName ?? "video-card-fallback"}
          title={String(rawUrl).slice(0, 500)}
        >
          Can&apos;t load preview
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`video-card-thumb-hit ${className ?? ""}`}
      onClick={open}
      onKeyDown={onKey}
      aria-label={ariaLabel || "Open video"}
      title="Click for large preview"
    >
      <video
        ref={videoRef}
        key={src}
        className="video-card-thumb-video"
        src={src}
        muted
        playsInline
        preload="auto"
        tabIndex={-1}
        onError={onVideoError}
        onPlay={(e) => e.currentTarget.pause()}
      />
    </button>
  );
}
